import { FC, useState } from 'react';
import { UserProfile } from '@src/types/users';
import { Checkbox } from '@src/components/Checkbox';
import { Button } from '@features/ui';
import { format } from 'date-fns';
import { savePaymentMethod, inviteRiseworks } from '@src/services/users';
import { toast } from 'react-toastify';

interface PayoutInfoProps {
  user: UserProfile | null;
  refreshUser?: () => void;
}

export const PayoutInfo: FC<PayoutInfoProps> = ({ user, refreshUser }) => {
  const [payoutMethods, setPayoutMethods] = useState({
    allow_riseworks: user?.allow_riseworks ?? false,
    allow_wire: user?.allow_wire ?? false,
    allow_paypal: user?.allow_paypal ?? false,
    allow_coupon: user?.allow_coupon ?? false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  if (!user) return null;

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch {
      return '';
    }
  };

  const handleCheckboxChange = (key: keyof typeof payoutMethods) => {
    setPayoutMethods((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSavePayoutMethods = async () => {
    if (!user?.id) return;

    try {
      setIsSaving(true);
      await savePaymentMethod(user.id, payoutMethods);
      toast.success('Payout methods saved successfully');
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail || 'Failed to save payout methods';

      toast.error(errorMessage);
      console.error('Error saving payout methods:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleInviteRiseworks = async () => {
    if (!user?.id) return;

    try {
      setIsInviting(true);
      await inviteRiseworks(user.id);
      toast.success('Riseworks invitation sent successfully');
      if (refreshUser) {
        refreshUser();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail || 'Failed to invite to Riseworks';

      toast.error(errorMessage);
      console.error('Error inviting to Riseworks:', error);
    } finally {
      setIsInviting(false);
    }
  };

  const getStatusText = (): string => {
    if (!user.rise_works_status) {
      return 'not invited';
    }

    if (user.rise_works_status.includes('completed')) {
      return 'completed';
    }

    if (user.rise_works_status.includes('pending')) {
      return 'pending';
    }

    return 'onboarding';
  };

  const shouldShowInviteButton = !user.rise_works_status;

  return (
    <div className="py-4 pr-8">
      {/* Save Button */}
      <div className="mb-8">
        <Button
          className="bg-[#007AFE] hover:bg-[#0066D6] text-white px-8 py-2.5 rounded-md text-sm font-semibold uppercase"
          disabled={isSaving}
          onClick={handleSavePayoutMethods}
        >
          {isSaving ? 'SAVING...' : 'SAVE PAYOUT METHODS'}
        </Button>
      </div>

      {/* Allow Payouts via Section */}
      <div className="flex gap-32">
        <div className="flex-shrink-0">
          <h3 className="text-white text-base font-normal mb-6">
            Allow Payouts via
          </h3>

          <div className="space-y-3.5">
            <Checkbox
              checked={payoutMethods.allow_riseworks}
              onChange={() => handleCheckboxChange('allow_riseworks')}
            >
              Riseworks
            </Checkbox>

            <Checkbox
              checked={payoutMethods.allow_wire}
              onChange={() => handleCheckboxChange('allow_wire')}
            >
              Bank transfer (ACH for US clients)
            </Checkbox>

            <Checkbox
              checked={payoutMethods.allow_paypal}
              onChange={() => handleCheckboxChange('allow_paypal')}
            >
              Paypal
            </Checkbox>

            <Checkbox
              checked={payoutMethods.allow_coupon}
              onChange={() => handleCheckboxChange('allow_coupon')}
            >
              Coupon
            </Checkbox>
          </div>
        </div>

        {/* Rise Logo and Status Section */}
        <div className="flex-1">
          <div className="flex flex-col items-start gap-6">
            <div className="flex-shrink-0">
              <img
                alt="Rise Logo"
                className="h-16 w-auto"
                src="/assets/rise-logo-white.png"
              />
            </div>

            <div className="flex-1">
              <div className="mb-2">
                <span className="text-white font-semibold">
                  Signup status:{' '}
                </span>
                <span className="text-white">{getStatusText()}</span>
              </div>

              {user.rise_works_invited &&
                user.rise_works_confirmed &&
                user.rise_works_wallet &&
                user.rise_works_wallet.startsWith('0x') && (
                  <>
                    <div className="text-white/80 text-sm mb-1">
                      <span className="text-white/60">Rise ID: </span>
                      {user.rise_works_wallet}
                    </div>
                    <div className="text-white/80 text-sm">
                      <span className="text-white/60">Completed: </span>
                      {formatDate(user.rise_works_confirmed)}
                    </div>
                  </>
                )}

              {!user.rise_works_confirmed && user.rise_works_invited && (
                <div className="text-white/80 text-sm">
                  <span className="text-white/60">Invited: </span>
                  {formatDate(user.rise_works_invited)}
                </div>
              )}

              {shouldShowInviteButton && (
                <div className="mt-4">
                  <Button
                    className="bg-[#007AFE] hover:bg-[#0066D6] text-white px-5 py-2 rounded-md text-sm font-medium"
                    disabled={isInviting}
                    onClick={handleInviteRiseworks}
                  >
                    {isInviting ? 'Inviting...' : 'Invite to Riseworks'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
